import { useMyWork } from '../api/queries'
import { useAuth } from '../auth/AuthProvider'
import { ProjectCard } from '../components/ProjectCard'
import { MyDayCard } from '../components/MyDayCard'
import { TodayCelebrationLine } from '../components/Celebrations'
import { OnCallLine } from '../components/IntranetOnCall'
import { QuickNoteButton } from '../components/QuickNoteButton'
import { ServiceDueCard } from '../components/ServiceDueCard'
import { TaskItem } from '../components/TaskItem'
import { EmptyState, ErrorBox, Loading, Section } from '../components/ui'
import type { Task } from '../api/types'
import { daysUntil } from '../lib/format'

/**
 * Forsiden for den enkelte bruger (kravspec afsnit 12 og 47).
 * Samme side bruges på desktop og mobil - layoutet skifter via CSS.
 */
export function HomePage() {
  const { user, isProjectManager } = useAuth()
  const { data, isLoading, error } = useMyWork()

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return <EmptyState>Ingen data.</EmptyState>

  const today = data.myTasks.filter((task) => {
    const days = daysUntil(task.deadline)
    return days !== null && days <= 0
  })

  return (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        Hej {user?.displayName.split(' ')[0]} - her er dit overblik.
      </p>

      {/* Foedselsdage og jubilaeer i dag (beslutning 117). */}
      <TodayCelebrationLine />

      {/* Hvem har tilkaldevagten (beslutning 120). */}
      <OnCallLine />

      {/* Hurtigknappen staar allerfoerst: den, der aabner appen midt paa dagen,
          goer det for at faa noget ud af hovedet (beslutning 082). */}
      <div style={{ marginBottom: 18 }}>
        <QuickNoteButton />
      </div>

      {/* Dagens opgaver staar derefter: det er det foerste, en montoer skal se,
          naar han aabner appen om morgenen (beslutning 061). */}
      <div style={{ marginBottom: 18 }}>
        <MyDayCard />
      </div>

      {/* Serviceoverblikket er til dem, der planlægger - ikke til alle. */}
      {isProjectManager && (
        <div style={{ marginBottom: 18 }}>
          <ServiceDueCard />
        </div>
      )}

      {data.overdue.length > 0 && (
        <Section title="Forsinkede opgaver" count={data.overdue.length}>
          <TaskList tasks={data.overdue} />
        </Section>
      )}

      {today.length > 0 && (
        <Section title="Opgaver i dag" count={today.length}>
          <TaskList tasks={today} />
        </Section>
      )}

      {data.waitingOnMe.length > 0 && (
        <Section title="Afventer mig" count={data.waitingOnMe.length}>
          <TaskList tasks={data.waitingOnMe} />
        </Section>
      )}

      {data.dueSoon.length > 0 && (
        <Section title="Deadline snart" count={data.dueSoon.length}>
          <TaskList tasks={data.dueSoon} />
        </Section>
      )}

      <Section title="Mine opgaver" count={data.myTasks.length}>
        {data.myTasks.length === 0 ? (
          <EmptyState>Du har ingen åbne opgaver lige nu.</EmptyState>
        ) : (
          <TaskList tasks={data.myTasks} />
        )}
      </Section>

      <Section title="Mine projekter" count={data.myProjects.length}>
        {data.myProjects.length === 0 ? (
          <EmptyState>Du er ikke tilknyttet nogen projekter endnu.</EmptyState>
        ) : (
          <div className="grid grid--projects">
            {data.myProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </Section>
    </>
  )
}

function TaskList({ tasks }: { tasks: Task[] }) {
  return (
    <div className="stack">
      {tasks.map((task) => (
        <TaskItem key={task.id} task={task} />
      ))}
    </div>
  )
}
